import { Button, debounce, Stack, useTheme } from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { useCallback, useState } from 'react';
import { MetaTags } from 'src/components/MetaTags/meta-tags';
import { SearchBar } from 'src/components/SearchBar/search-bar';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useCustomViews } from 'src/graphql/entities/custom-view';
import { MAP_URL_ENTITY_NAME_TO_ENTITY } from 'src/pages/CustomViews/constants';
import { SELECTED_VIEWS_BY_ENTITY } from 'src/pages/CustomViews/custom-views-selected-view/constants';
import { useCopyCustomView } from 'src/pages/CustomViews/hooks/use-copy-custom-view';
import { useGlobalStore } from 'src/store';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { CustomViewsList } from './custom-views-list';

export const CustomViewsListView = () => {
  const theme = useTheme();
  const { navigate } = useNavigateRoute();
  const partnerId = useGlobalStore.use.selectedPartnerId() ?? PLATFORM_PARTNER_ID;

  const { entityName } = useRouteParams(UrlParams.entityName);

  const selectedEntity = MAP_URL_ENTITY_NAME_TO_ENTITY[entityName] ?? entityName;

  const [searchTerm, setSearchTerm] = useState('');

  const { customViews, toggleFavorite, deleteCustomView } = useCustomViews({
    entity: selectedEntity,
    partnerId,
    searchTerm,
  });

  const handleCreateViewClick = useCallback(
    () => navigate(portalPaths.customView.action(entityName)),
    [navigate, entityName],
  );

  const handleUpdateViewClick = useCallback(
    (view: string) => navigate(portalPaths.customView.action(entityName, view)),
    [navigate, entityName],
  );

  const { goToCopyView: handleCopyViewClick } = useCopyCustomView();

  const handleFavoriteClick = useCallback(
    (id: string, isFavorite: boolean) => toggleFavorite(id, { isFavorite }),
    [toggleFavorite],
  );

  const handleOpenViewClick = useCallback(
    (viewId: string) => {
      navigate(portalPaths.entity.selected(entityName, viewId));
    },
    [navigate, entityName],
  );

  const onSearchInputChange = debounce((value: string) => {
    setSearchTerm(value);
  }, SEARCH_DEBOUNCE_DELAY);

  return (
    <MetaTags title="Views">
      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        paddingY={theme.spacing(1.5)}
        sx={{
          borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
        }}
        paddingRight={theme.spacing(1.5)}
      >
        <Stack
          flexDirection="row"
          alignItems="center"
          flexWrap="wrap"
          sx={{
            order: { xs: -1, sm: 1 },
            alignSelf: 'flex-end',
            gap: {
              xs: '0.5rem',
              sm: '1rem',
            },
          }}
        >
          <SearchBar
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onSearchInputChange(e.target.value);
            }}
          />
          <Button
            color="primary"
            variant="contained"
            size="small"
            onClick={handleCreateViewClick}
          >
            + New View
          </Button>
        </Stack>
      </Stack>

      <CustomViewsList
        rows={customViews}
        onClick={
          // we support opening only specific entities
          SELECTED_VIEWS_BY_ENTITY[selectedEntity] ? handleOpenViewClick : undefined
        }
        onCopy={handleCopyViewClick}
        onDelete={deleteCustomView}
        onUpdate={handleUpdateViewClick}
        onFavorite={handleFavoriteClick}
      />
    </MetaTags>
  );
};
